import { ProductRateRow } from '@/network/graphql.g'
import { FC } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  MoreInfoProps,
  TranslatedReviewsProps,
  originLanguageProps
} from './reviews'
import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { Rating } from 'react-simple-star-rating'
import { CheckIconFilledDefault } from '@/components/atoms/icons/CheckIconFilledDefaut'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { applyImagePolicy } from '@/utils/imageExtension'
import { Small3, Tiny2 } from '@/components/atoms/typography'
import AdditionalRatingInfo from './additionalRatingInfo'
import { useDomain } from '@/providers/storeProvider'
import useTranslation from 'next-translate/useTranslation'
import { limitString, dateFormater, hideCustomerName } from './helper'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'

type ReviewsTabletMobileProps = {
  review: ProductRateRow
  translatedReviews: TranslatedReviewsProps
  fetchTranslateReview: (reviewId: string) => void
  isOriginLanguage: originLanguageProps
  handleOriginLanguageClick: (reviewId: string) => void
  handleMoreClick: (reviewId: string) => void
  handleMoreInfoClick: (reviewId: string) => void
  isMoreOpen: MoreInfoProps
  isMoreInfoOpen: MoreInfoProps
}

const ReviewTabletMobile: FC<ReviewsTabletMobileProps> = ({
  review,
  translatedReviews,
  fetchTranslateReview,
  isOriginLanguage,
  handleOriginLanguageClick,
  handleMoreClick,
  handleMoreInfoClick,
  isMoreOpen,
  isMoreInfoOpen
}) => {
  const theme = useTheme()
  const { subdomainName } = useDomain()
  const { t } = useTranslation('common')
  const {
    customerName,
    createdAt,
    rate,
    description,
    id,
    showName,
    rateColor,
    rateMaterial,
    rateSize,
    languageCode,
    colorValue,
    productId,
    sizeValue
  } = review ?? {}

  const translatedOrOriginDescription = !isOriginLanguage[id]
    ? description
    : translatedReviews[id] || description

  const isSubdomainLanguage = languageCode === subdomainName
  const showAdditionalRatingInfo =
    rateColor !== 0 && rateMaterial !== 0 && rateSize !== 0

  const handleTranslate = () => {
    if (!translatedReviews[id]) {
      fetchTranslateReview(id)
    }
    handleOriginLanguageClick(id)
  }

  return (
    <>
      <BaseInfoMobile>
        <Box>
          <Rating
            readonly
            initialValue={rate}
            allowFraction={true}
            size={14}
            fillColor={theme.colors.sizeTableDescriptionr}
          />
        </Box>
        <StyledFlex>
          <CheckIconFilledDefault />
          <StyledSmall3>
            {t('ProductDetail.Reviews.verifiedPurchase')}
          </StyledSmall3>
        </StyledFlex>
        <StyledFlex alignItems="center">
          <Box>
            <ResponsiveImage
              imageUrl={applyImagePolicy(`/flags/${languageCode}.png`)}
              layout="fixed"
              width={16}
              height={11}
            />
          </Box>
          <Tiny2 m={0}>
            {showName === true ? customerName : hideCustomerName(customerName)}
          </Tiny2>
        </StyledFlex>
        <ReviewDate>{dateFormater(createdAt)}</ReviewDate>
      </BaseInfoMobile>
      {description ? (
        <>
          <Description m={0}>
            {!isMoreOpen[id]
              ? limitString(translatedOrOriginDescription, 50) + ' '
              : translatedOrOriginDescription + ' '}
            {translatedOrOriginDescription.length > 50 ? (
              <StyledSpan onClick={() => handleMoreClick(id)}>
                {isMoreOpen[id]
                  ? t('ProductDetail.Reviews.less')
                  : t('ProductDetail.Reviews.more')}
              </StyledSpan>
            ) : null}
          </Description>
          {!isOriginLanguage[id] && !isSubdomainLanguage && (
            <StyledButton onClick={handleTranslate}>
              {t('ProductDetail.Reviews.translate')}
            </StyledButton>
          )}
          {isOriginLanguage[id] && (
            <StyledButton onClick={() => handleOriginLanguageClick(id)}>
              {t('ProductDetail.Reviews.originalLanguage')}
            </StyledButton>
          )}
        </>
      ) : null}
      {isMoreInfoOpen[id] && (
        <>
          <StyledFlex2>
            {colorValue && (
              <StyledTiny2>
                {t('Category.Filter.color')}:
                {productId ? (
                  <Link
                    href={`${Routes.product}/${productId}`}
                    textDecoration="underline"
                  >
                    {colorValue}
                  </Link>
                ) : (
                  colorValue
                )}
              </StyledTiny2>
            )}
            {colorValue && <StyledTiny2>{'|'}</StyledTiny2>}
            {sizeValue && (
              <StyledTiny2>
                {t('Category.Filter.size')}:<span>{sizeValue}</span>
              </StyledTiny2>
            )}
          </StyledFlex2>
          {showAdditionalRatingInfo ? (
            <AdditionalRatingInfo productRateRow={review} inReview />
          ) : null}
        </>
      )}
      <StyledButton onClick={() => handleMoreInfoClick(id)}>
        {isMoreInfoOpen[id]
          ? t('ProductDetail.Reviews.lessInformation')
          : t('ProductDetail.Reviews.moreInformation')}
      </StyledButton>
    </>
  )
}

export default ReviewTabletMobile

const BaseInfoMobile = styled(Grid)`
  grid-template-columns: 1fr 3fr;
  grid-template-rows: 1fr 1fr;
  grid-gap: 10px;
  align-items: center;
`

const StyledFlex = styled(Flex)`
  gap: 8px;
`

const StyledFlex2 = styled(Flex)`
  gap: 3px;
`

const StyledSpan = styled.span`
  text-decoration: underline;
`

const Description = styled(Tiny2)`
  font-weight: 400;
`

const ReviewDate = styled(Tiny2)`
  font-weight: 400;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewDate};
`

const StyledTiny2 = styled(Tiny2)`
  font-weight: 400;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewInfoNeutral};
  display: flex;
  gap: 2px;
`

const StyledSmall3 = styled(Small3)`
  font-weight: 500;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewVerified};
`

const StyledButton = styled.button`
  border: none;
  color: ${(props) => props.theme.colors.onBackground};
  background-color: inherit;
  text-decoration: underline;
  font-weight: 400;
  font-size: 12px;
  max-width: fit-content;
  padding: 0;
  cursor: pointer;
`
